import React, { useCallback, useContext, useMemo, useState, useEffect } from 'react'
import { TransactionResponse } from '@ethersproject/providers'
import { Currency, TokenAmount, ETHER, currencyEquals, Token } from '@uniswap/sdk-core'
import { WETH9 } from '@uniswap/sdk-core'
import { AlertTriangle, AlertCircle } from 'react-feather'
import ReactGA from 'react-ga'
import { useV3Factory, useV3NFTPositionManagerContract } from '../../hooks/useContract'
import { RouteComponentProps } from 'react-router-dom'
import { Text } from 'rebass'
import { ThemeContext } from 'styled-components'
import { ButtonError, ButtonLight, ButtonPrimary } from '../../components/Button'
import { YellowCard, OutlineCard, BlueCard, LightCard } from '../../components/Card'
import { AutoColumn, ColumnCenter } from '../../components/Column'
import TransactionConfirmationModal, { ConfirmationModalContent } from '../../components/TransactionConfirmationModal'
import CurrencyInputPanel from '../../components/CurrencyInputPanel'
import { RowBetween } from '../../components/Row'
import { useIsSwapUnsupported } from '../../hooks/useIsSwapUnsupported'
import { useUSDCValue } from '../../hooks/useUSDCPrice'
import Review from './Review'
import { useActiveWeb3React } from '../../hooks'
import { useCurrency } from '../../hooks/Tokens'
import { ApprovalState, useApproveCallback } from '../../hooks/useApproveCallback'
import useTransactionDeadline from '../../hooks/useTransactionDeadline'
import { useWalletModalToggle } from '../../state/application/hooks'
import { Field, Bound } from '../../state/mint/v3/actions'
import { v4 as uuidv4 } from 'uuid'

import { useTransactionAdder } from '../../state/transactions/hooks'
import { useIsExpertMode, useUserSlippageTolerance } from '../../state/user/hooks'
import { TYPE } from '../../theme'
import { maxAmountSpend } from '../../utils/maxAmountSpend'
import { Dots } from '../Pool/styleds'
import { currencyId } from '../../utils/currencyId'
import UnsupportedCurrencyFooter from 'components/swap/UnsupportedCurrencyFooter'
import {
  DynamicSection,
  CurrencyDropdown,
  StyledInput,
  Wrapper,
  ScrollablePage,
  RowContainer,
  CustomAppBody,
  StepperContainer,
  Step,
  StepNumber,
  StepContent,
  StepLine,
  StepTitle,
  StepDescription,
  StepperWrapper,
  StepperHeader,
  MobileStepperWrapper,
  StepReview,
} from './styled'
import { useTranslation } from 'react-i18next'
import {
  useV3MintState,
  useV3MintActionHandlers,
  useRangeHopCallbacks,
  useV3DerivedMintInfo,
} from 'state/mint/v3/hooks'
import { FeeAmount, NonfungiblePositionManager } from '@uniswap/v3-sdk'
import { NONFUNGIBLE_POSITION_MANAGER_ADDRESSES } from 'constants/v3'
import { useV3PositionFromTokenId } from 'hooks/useV3Positions'
import { useDerivedPositionInfo } from 'hooks/useDerivedPositionInfo'
import { PositionPreview } from 'components/PositionPreview'
import FeeSelector from 'components/FeeSelector'
import RangeSelector from 'components/RangeSelector'
import RateToggle from 'components/RateToggle'
import { BigNumber } from '@ethersproject/bignumber'
import { calculateGasMargin } from 'utils'
import { Helmet } from 'react-helmet'
import DoubleCurrencyLogo from 'components/DoubleLogo'
import Tag from 'components/Tag'
import { formatPriceString } from 'utils/formatTokenAmount'
import { useAllV3Ticks } from 'hooks/useAllV3Ticks'
import { CURRENT_CHAIN } from '@hyperswap/addresses'
import { API_GASLESS_BASE_URL } from 'constants/links'
import { generateSignature } from 'utils/cryptoUtils'
import axios from 'axios'
import { AddLiquidityAddRemoveTabs } from 'components/NavigationTabs/AddLiquidityNavigationTabs'
import TagButton from 'components/TagButton'

const FeeLabel = {
  [FeeAmount.LOWER]: '0.01%',
  [FeeAmount.LOW]: '0.05%',
  [FeeAmount.MEDIUM]: '0.3%',
  [FeeAmount.HIGH]: '1%',
}

export default function AddLiquidity({
  match: {
    params: { currencyIdA, currencyIdB, feeAmount: feeAmountFromUrl, tokenId },
  },
  history,
}: RouteComponentProps<{ currencyIdA?: string; currencyIdB?: string; feeAmount?: string; tokenId?: string }>) {
  const { t } = useTranslation()

  const { account, chainId, library } = useActiveWeb3React()
  const theme = useContext(ThemeContext)
  const toggleWalletModal = useWalletModalToggle() // toggle wallet when disconnected
  const expertMode = useIsExpertMode()
  const addTransaction = useTransactionAdder()
  const positionManager = useV3NFTPositionManagerContract()
  const factoryContract = useV3Factory()

  // check for existing position if tokenId in url
  const { position: existingPositionDetails, loading: positionLoading } = useV3PositionFromTokenId(
    tokenId ? BigNumber.from(tokenId) : undefined
  )
  const hasExistingPosition = !!existingPositionDetails && !positionLoading
  const { position: existingPosition } = useDerivedPositionInfo(existingPositionDetails)

  // fee selection from url
  const feeAmount: FeeAmount | undefined =
    feeAmountFromUrl && Object.values(FeeAmount).includes(parseFloat(feeAmountFromUrl))
      ? parseFloat(feeAmountFromUrl)
      : FeeAmount.MEDIUM

  const currencyA = useCurrency(currencyIdA)
  const currencyB = useCurrency(currencyIdB)
  const { exists } = useAllV3Ticks(
    currencyA instanceof Token ? currencyA : WETH9[CURRENT_CHAIN],
    currencyB instanceof Token ? currencyB : WETH9[CURRENT_CHAIN],
    feeAmount
  )

  const [isMaxPriceRange, setMaxPriceRange] = React.useState(false)

  // keep track for UI display purposes of user selected base currency
  const [baseCurrency, setBaseCurrency] = useState(currencyA)
  const quoteCurrency = useMemo(
    () =>
      currencyA && currencyB && baseCurrency
        ? currencyEquals(baseCurrency, currencyA)
          ? currencyB
          : currencyA
        : undefined,
    [currencyA, currencyB, baseCurrency]
  )

  // url params are the source truth, so we have to do this
  useEffect(() => {
    setBaseCurrency(currencyA)
    return () => {
      setBaseCurrency(undefined)
    }
  }, [currencyA, currencyB])

  // mint state
  const { independentField, typedValue, startPriceTypedValue } = useV3MintState()

  const {
    pool,
    ticks,
    dependentField,
    price,
    pricesAtTicks,
    parsedAmounts,
    currencyBalances,
    position,
    noLiquidity,
    currencies,
    errorMessage,
    invalidPool,
    invalidRange,
    outOfRange,
    depositADisabled,
    depositBDisabled,
    invertPrice,
  } = useV3DerivedMintInfo(
    currencyA ?? undefined,
    currencyB ?? undefined,
    feeAmount,
    baseCurrency ?? undefined,
    existingPosition
  )

  const {
    onFieldAInput,
    onFieldBInput,
    onLeftRangeInput,
    onRightRangeInput,
    onStartPriceInput,
  } = useV3MintActionHandlers(noLiquidity)

  const isValid = !errorMessage && !invalidRange

  // modal and loading
  const [showConfirm, setShowConfirm] = useState<boolean>(false)
  const [attemptingTxn, setAttemptingTxn] = useState<boolean>(false) // clicked confirm

  // txn values
  const deadline = useTransactionDeadline() // custom from users settings
  const [allowedSlippage] = useUserSlippageTolerance() // custom from users
  const [txHash, setTxHash] = useState<string>('')

  // get formatted amounts
  const formattedAmounts = {
    [independentField]: typedValue,
    [dependentField]: parsedAmounts[dependentField]?.toSignificant(6) ?? '',
  }

  const usdcValues = {
    [Field.CURRENCY_A]: useUSDCValue(parsedAmounts[Field.CURRENCY_A]),
    [Field.CURRENCY_B]: useUSDCValue(parsedAmounts[Field.CURRENCY_B]),
  }

  // get the max amounts user can add
  const maxAmounts: { [field in Field]?: TokenAmount } = [Field.CURRENCY_A, Field.CURRENCY_B].reduce(
    (accumulator, field) => {
      return {
        ...accumulator,
        [field]: maxAmountSpend(currencyBalances[field]),
      }
    },
    {}
  )

  const atMaxAmounts: { [field in Field]?: TokenAmount } = [Field.CURRENCY_A, Field.CURRENCY_B].reduce(
    (accumulator, field) => {
      return {
        ...accumulator,
        [field]: maxAmounts[field]?.equalTo(parsedAmounts[field] ?? '0'),
      }
    },
    {}
  )

  // check whether the user has approved the router on the tokens
  const [approvalA, approveACallback] = useApproveCallback(
    parsedAmounts[Field.CURRENCY_A],
    chainId ? NONFUNGIBLE_POSITION_MANAGER_ADDRESSES[chainId] : undefined
  )
  const [approvalB, approveBCallback] = useApproveCallback(
    parsedAmounts[Field.CURRENCY_B],
    chainId ? NONFUNGIBLE_POSITION_MANAGER_ADDRESSES[chainId] : undefined
  )

  async function onAdd() {
    if (!chainId || !library || !account) return

    if (!positionManager || !currencyA || !currencyB) {
      return
    }

    if (position && account && deadline) {
      const { calldata, value } =
        hasExistingPosition && tokenId
          ? NonfungiblePositionManager.addCallParameters(position, {
              tokenId,
              slippageTolerance: allowedSlippage,
              deadline: deadline.toString(),
              useEther: currencyA === ETHER || currencyB === ETHER,
            })
          : NonfungiblePositionManager.addCallParameters(position, {
              slippageTolerance: allowedSlippage,
              recipient: account,
              deadline: deadline.toString(),
              useEther: currencyA === ETHER || currencyB === ETHER,
              createPool: noLiquidity,
            })

      const txn = {
        to: NONFUNGIBLE_POSITION_MANAGER_ADDRESSES[chainId],
        data: calldata,
        value,
      }

      setAttemptingTxn(true)

      library
        .getSigner()
        .estimateGas(txn)
        .then((estimate) => {
          console.log('AddLiquidity V3 gasLimit', calculateGasMargin(estimate).toString())
          const newTxn = {
            ...txn,
            gasLimit: calculateGasMargin(estimate),
          }

          return library
            .getSigner()
            .sendTransaction(newTxn)
            .then((response: TransactionResponse) => {
              setAttemptingTxn(false)
              addTransaction(response, {
                summary: noLiquidity
                  ? `Create pool and add ${currencyA?.symbol}/${currencyB?.symbol} V3 liquidity`
                  : `Add ${currencyA?.symbol}/${currencyB?.symbol} V3 liquidity`,
              })
              setTxHash(response.hash)
              ReactGA.event({
                category: 'Liquidity',
                action: 'Add',
                label: [currencies[Field.CURRENCY_A]?.symbol, currencies[Field.CURRENCY_B]?.symbol].join('/'),
              })
            })
        })
        .catch((error) => {
          setAttemptingTxn(false)
          // we only care if the error is something _other_ than the user rejected the tx
          if (error?.code !== 4001) {
            console.error(error)
          }
        })
    } else {
      return
    }
  }

  const pendingText = `Supplying ${!depositADisabled ? parsedAmounts[Field.CURRENCY_A]?.toSignificant(6) : ''} ${
    !depositADisabled ? currencies[Field.CURRENCY_A]?.symbol : ''
  } ${!outOfRange ? 'and' : ''} ${!depositBDisabled ? parsedAmounts[Field.CURRENCY_B]?.toSignificant(6) : ''} ${
    !depositBDisabled ? currencies[Field.CURRENCY_B]?.symbol : ''
  }`

  const handleCurrencySelect = useCallback(
    (currencyNew: Currency, currencyIdOther?: string): (string | undefined)[] => {
      const currencyIdNew = currencyId(currencyNew)

      if (currencyIdNew === currencyIdOther) {
        // not ideal, but for now clobber the other if the currency ids are equal
        return [currencyIdNew, undefined]
      } else {
        // prevent weth + eth
        const isETHOrWETHNew =
          currencyIdNew === 'HYPE' || (chainId !== undefined && currencyIdNew === WETH9[chainId]?.address)
        const isETHOrWETHOther =
          currencyIdOther !== undefined &&
          (currencyIdOther === 'HYPE' || (chainId !== undefined && currencyIdOther === WETH9[chainId]?.address))

        if (isETHOrWETHNew && isETHOrWETHOther) {
          return [currencyIdNew, undefined]
        } else {
          return [currencyIdNew, currencyIdOther]
        }
      }
    },
    [chainId]
  )

  const handleCurrencyASelect = useCallback(
    (currencyANew: Currency) => {
      const [idA, idB] = handleCurrencySelect(currencyANew, currencyIdB)
      if (idB === undefined) {
        history.push(`/add/${idA}`)
      } else {
        history.push(`/add/${idA}/${idB}`)
      }

      onLeftRangeInput('')
      onRightRangeInput('')
    },
    [handleCurrencySelect, currencyIdB, onLeftRangeInput, onRightRangeInput, history]
  )

  const handleCurrencyBSelect = useCallback(
    (currencyBNew: Currency) => {
      const [idB, idA] = handleCurrencySelect(currencyBNew, currencyIdA)
      if (idA === undefined) {
        history.push(`/add/${idB}`)
      } else {
        history.push(`/add/${idA}/${idB}`)
      }
      onLeftRangeInput('')
      onRightRangeInput('')
    },
    [handleCurrencySelect, currencyIdA, onLeftRangeInput, onRightRangeInput, history]
  )

  const handleFeePoolSelect = useCallback(
    (newFeeAmount: FeeAmount) => {
      history.push(`/add/${currencyIdA}/${currencyIdB}/${newFeeAmount}`)
    },
    [currencyIdA, currencyIdB, history]
  )

  const handleDismissConfirmation = useCallback(() => {
    setShowConfirm(false)
    // if there was a tx hash, we want to clear the input
    if (txHash) {
      onFieldAInput('')
      history.push('/pool')
    }
    setTxHash('')
  }, [history, onFieldAInput, txHash])

  const addIsUnsupported = useIsSwapUnsupported(currencies?.CURRENCY_A, currencies?.CURRENCY_B)

  const clearAll = useCallback(() => {
    onFieldAInput('')
    onFieldBInput('')
    onLeftRangeInput('')
    onRightRangeInput('')
    history.push(`/add/HYPE`)
    history.go(0)
  }, [history, onFieldAInput, onFieldBInput, onLeftRangeInput, onRightRangeInput])

  // get value and prices at ticks
  const { [Bound.LOWER]: tickLower, [Bound.UPPER]: tickUpper } = ticks
  const { [Bound.LOWER]: priceLower, [Bound.UPPER]: priceUpper } = pricesAtTicks

  const { getDecrementLower, getIncrementLower, getDecrementUpper, getIncrementUpper } = useRangeHopCallbacks(
    baseCurrency ?? undefined,
    quoteCurrency ?? undefined,
    feeAmount,
    tickLower,
    tickUpper,
    pool
  )

  // we need an existence check on parsed amounts for single-asset deposits
  const showApprovalA = approvalA !== ApprovalState.APPROVED && !!parsedAmounts[Field.CURRENCY_A]
  const showApprovalB = approvalB !== ApprovalState.APPROVED && !!parsedAmounts[Field.CURRENCY_B]

  const steps = [
    { title: 'Step 1', description: 'Select token pair and fees' },
    { title: 'Step 2', description: 'Set price range' },
    { title: 'Step 3', description: 'Create pool' },
    { title: 'Step 4', description: 'Enter deposit amounts' },
  ]

  const [activeStep, setActiveStep] = useState(0)

  const handleStep = (newStep: number, force?: boolean) => {
    if (activeStep === 1 && newStep === 2 && step2Validate) {
      setActiveStep(3)
    } else if (
      newStep < activeStep ||
      (newStep === 1 && step0Validate) ||
      (newStep === 2 && step1Validate) ||
      (newStep === 3 && step2Validate)
    ) {
      setActiveStep(newStep)
    } else if (force === true) {
      setActiveStep(newStep)
    }
  }

  const [initAddExistingPosition, setInitAddExistingPosition] = useState(false)
  useEffect(() => {
    if (hasExistingPosition && existingPosition && initAddExistingPosition === false) {
      handleStep(2)
      setInitAddExistingPosition(true)
    }
    // eslint-disable-next-line
  }, [hasExistingPosition, existingPosition, initAddExistingPosition])

  const [loadingCreate, setLoadingCreate] = useState(false)
  const [errorCreate, setErrorCreate] = useState('')
  const [successCreate, setSuccessCreate] = useState(false)
  const pairDoesNotExists = !exists

  async function onCreate() {
    if (!chainId || !library || !account || !factoryContract) return
    setLoadingCreate(true)
    const currencyAAddress = currencyA instanceof Token ? currencyA.address : WETH9[CURRENT_CHAIN].address
    const currencyBAddress = currencyB instanceof Token ? currencyB.address : WETH9[CURRENT_CHAIN].address

    const currencyASymbol = currencyA?.symbol
    const currencyBSymbol = currencyB?.symbol

    const timestamp = Math.floor(Date.now() / 1000)
    const message = `Please sign this message to create pair ${currencyASymbol}-${currencyBSymbol}. Timestamp: ${timestamp}`

    try {
      const signer = library.getSigner()
      const signature = await signer.signMessage(message)
      const uuid = uuidv4()
      setErrorCreate('')
      setSuccessCreate(false)

      const checkStatus = async () => {
        factoryContract.on('PoolCreated', (token0, token1, fee, poolAddress) => {
          if (
            ((token0.toLowerCase() === currencyAAddress.toLowerCase() &&
              token1.toLowerCase() === currencyBAddress.toLowerCase()) ||
              (token0.toLowerCase() === currencyBAddress.toLowerCase() &&
                token1.toLowerCase() === currencyAAddress.toLowerCase())) &&
            fee === feeAmount
          ) {
            console.log(`LP address: ${poolAddress}`)
            factoryContract.removeAllListeners('PoolCreated')
            setLoadingCreate(false)
            setSuccessCreate(true)
            setErrorCreate('')
            handleStep(3, true)
          }
        })
      }

      const body = JSON.stringify({
        taskId: uuid,
        address: account,
        signature: signature,
        message: message,
        tokenA: currencyAAddress,
        tokenB: currencyBAddress,
        version: 'v3',
        fee: `${feeAmount}`,
      })

      const config = {
        method: 'post',
        url: `${API_GASLESS_BASE_URL}/api/relay-create-pair`,
        headers: {
          'x-signature': generateSignature(message),
          'Content-Type': 'application/json',
        },
        data: body,
      }

      axios
        .request(config)
        .then(async (response) => {
          console.log(response.data)
          if (response.data.success === true) {
            await checkStatus()
          } else {
            setLoadingCreate(false)
            setSuccessCreate(false)
            setErrorCreate('An error has occurred. Please try again.')
            console.log(response)
          }
        })
        .catch((error: any) => {
          setLoadingCreate(false)
          setSuccessCreate(false)
          setErrorCreate('An error has occurred. Please try again.')
          console.error(error)
        })
    } catch (error) {
      setLoadingCreate(false)
      setSuccessCreate(false)
      console.error('Error sending transaction:', error)
    }
  }

  const step0Validate = currencies.CURRENCY_A && currencies.CURRENCY_B && feeAmount
  const step1Validate = !(tickLower === undefined || tickUpper === undefined || invalidPool || invalidRange)
  const step2Validate = !pairDoesNotExists || successCreate

  return (
    <>
      <Helmet>
        <title>Add Liquidity to V3 Pools | HyperSwap Exchange on Hyperliquid</title>
        <meta
          name="description"
          content="Provide concentrated liquidity to V3 pools on HyperSwap Exchange. Built on Hyperliquid, optimize your earnings with advanced DeFi strategies."
        />
        <meta
          name="keywords"
          content="HyperSwap V3 liquidity, concentrated liquidity, add liquidity, Hyperliquid blockchain, DeFi pools, Hyperliquid EVM"
        />
        <meta property="og:title" content="Add Liquidity to V3 Pools | HyperSwap Exchange on Hyperliquid" />
        <meta
          property="og:description"
          content="Add liquidity to V3 pools on HyperSwap Exchange, leveraging Hyperliquid's innovative blockchain technology."
        />
        <meta property="og:url" content={`${window.location.origin}/#/add/HYPE`} />
      </Helmet>
      <div>
        <AddLiquidityAddRemoveTabs />
        <ScrollablePage>
          <TransactionConfirmationModal
            isOpen={showConfirm}
            onDismiss={handleDismissConfirmation}
            attemptingTxn={attemptingTxn}
            hash={txHash}
            content={() => (
              <ConfirmationModalContent
                title={'Add Liquidity'}
                onDismiss={handleDismissConfirmation}
                topContent={() => (
                  <Review
                    parsedAmounts={parsedAmounts}
                    position={position}
                    existingPosition={existingPosition}
                    priceLower={priceLower}
                    priceUpper={priceUpper}
                    outOfRange={outOfRange}
                  />
                )}
                bottomContent={() => (
                  <ButtonPrimary style={{ marginTop: '1rem' }} onClick={onAdd}>
                    <Text fontWeight={500} fontSize={20}>
                      Add
                    </Text>
                  </ButtonPrimary>
                )}
              />
            )}
            pendingText={pendingText}
          />
          <AutoColumn>
            <RowContainer>
              <StepperWrapper>
                <CustomAppBody width="100%">
                  <StepperHeader>
                    <h2>New Concentrated Liquidity Position</h2>
                  </StepperHeader>
                  <StepperContainer>
                    {steps.map((step, index) => (
                      <Step
                        key={index}
                        clickable={
                          !!(index < activeStep || (index === 1 && step0Validate) || (index === 2 && step1Validate))
                        }
                        onClick={() => handleStep(index)}
                      >
                        <StepNumber active={index === activeStep}>{index + 1}</StepNumber>
                        {index < steps.length - 1 && <StepLine />}
                        <StepContent>
                          <StepTitle active={index === activeStep}>{step.title}</StepTitle>
                          <StepDescription active={index === activeStep}>{step.description}</StepDescription>
                        </StepContent>
                      </Step>
                    ))}
                  </StepperContainer>
                </CustomAppBody>
              </StepperWrapper>

              <MobileStepperWrapper>
                <CustomAppBody width="100%">
                  <StepperContainer>
                    <Step
                      key={activeStep}
                      clickable={
                        !!(
                          activeStep < activeStep ||
                          (activeStep === 1 && step0Validate) ||
                          (activeStep === 2 && step1Validate)
                        )
                      }
                      onClick={() => handleStep(activeStep)}
                    >
                      <StepNumber active={activeStep === activeStep}>{activeStep + 1}</StepNumber>
                      <StepContent>
                        <StepTitle active={activeStep === activeStep}>{steps[activeStep].title}</StepTitle>
                        <StepDescription active={activeStep === activeStep}>
                          {steps[activeStep].description}
                        </StepDescription>
                      </StepContent>
                    </Step>
                  </StepperContainer>
                </CustomAppBody>
              </MobileStepperWrapper>

              <CustomAppBody
                width={[0, 3].includes(activeStep) ? 'fit-content' : '100%'}
                height={activeStep === 0 ? 'fit-content' : ''}
              >
                <Wrapper>
                  {currencyA && currencyB && (
                    <div style={{ maxWidth: '68px', marginLeft: 'auto' }}>
                      <TagButton onClick={() => clearAll()}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="icon icon-tabler icons-tabler-outline icon-tabler-restore"
                        >
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M3.06 13a9 9 0 1 0 .49 -4.087" />
                          <path d="M3 4.001v5h5" />
                          <path d="M12 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
                        </svg>{' '}
                        Reset
                      </TagButton>
                    </div>
                  )}
                  <AutoColumn gap="32px">
                    {activeStep === 0 && (
                      <>
                        {!hasExistingPosition && (
                          <>
                            <AutoColumn gap="md">
                              <RowBetween paddingBottom="20px">
                                <TYPE.label>Select pair</TYPE.label>
                              </RowBetween>
                              <RowBetween>
                                <CurrencyDropdown
                                  value={formattedAmounts[Field.CURRENCY_A]}
                                  onUserInput={onFieldAInput}
                                  hideInput={true}
                                  onMax={() => {
                                    onFieldAInput(maxAmounts[Field.CURRENCY_A]?.toExact() ?? '')
                                  }}
                                  onCurrencySelect={handleCurrencyASelect}
                                  showMaxButton={!atMaxAmounts[Field.CURRENCY_A]}
                                  currency={currencies[Field.CURRENCY_A]}
                                  id="add-liquidity-input-tokena"
                                  showCommonBases
                                />
                                <div style={{ width: '12px' }}></div>

                                <CurrencyDropdown
                                  value={formattedAmounts[Field.CURRENCY_B]}
                                  hideInput={true}
                                  onUserInput={onFieldBInput}
                                  onCurrencySelect={handleCurrencyBSelect}
                                  onMax={() => {
                                    onFieldBInput(maxAmounts[Field.CURRENCY_B]?.toExact() ?? '')
                                  }}
                                  showMaxButton={!atMaxAmounts[Field.CURRENCY_B]}
                                  currency={currencies[Field.CURRENCY_B]}
                                  id="add-liquidity-input-tokenb"
                                  showCommonBases
                                />
                              </RowBetween>
                            </AutoColumn>
                          </>
                        )}
                        <>
                          {hasExistingPosition && existingPosition ? (
                            <PositionPreview
                              position={existingPosition}
                              title={'Selected Range'}
                              inRange={!outOfRange}
                            />
                          ) : (
                            <>
                              <FeeSelector
                                disabled={!currencyB || !currencyA}
                                feeAmount={feeAmount}
                                handleFeePoolSelect={handleFeePoolSelect}
                              />
                            </>
                          )}
                        </>

                        <ButtonPrimary disabled={!step0Validate} onClick={() => handleStep(1)}>
                          Continue
                        </ButtonPrimary>
                      </>
                    )}

                    {activeStep > 0 && step0Validate && (
                      <StepReview style={{ marginTop: '12px' }}>
                        <span className="title">Step 1</span>
                        <div className="value">
                          <DoubleCurrencyLogo
                            currency0={currencies.CURRENCY_A ?? undefined}
                            currency1={currencies.CURRENCY_B ?? undefined}
                            size={16}
                            margin={true}
                          />
                          <TYPE.label ml="8px" fontSize="14px">
                            {currencies.CURRENCY_A?.symbol} / {currencies.CURRENCY_B?.symbol}
                          </TYPE.label>
                          <div style={{ marginLeft: '10px' }}>
                            <Tag variant={2}>{feeAmount && FeeLabel[feeAmount]}</Tag>
                          </div>
                        </div>
                        <button onClick={() => handleStep(0)}>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1" />
                            <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z" />
                            <path d="M16 5l3 3" />
                          </svg>
                          Edit
                        </button>
                      </StepReview>
                    )}

                    {activeStep === 1 && (
                      <>
                        {noLiquidity && (
                          <DynamicSection disabled={!currencyA || !currencyB}>
                            <AutoColumn gap="md">
                              <RowBetween>
                                <TYPE.label>{t('selectStartingPrice')}</TYPE.label>
                                {baseCurrency && quoteCurrency ? (
                                  <RateToggle
                                    currencyA={baseCurrency}
                                    currencyB={quoteCurrency}
                                    handleRateToggle={() => {
                                      onLeftRangeInput('')
                                      onRightRangeInput('')
                                      history.push(
                                        `/add/${currencyIdB as string}/${currencyIdA as string}${
                                          feeAmount ? '/' + feeAmount : ''
                                        }`
                                      )
                                    }}
                                  />
                                ) : null}
                              </RowBetween>

                              <OutlineCard padding="12px">
                                <StyledInput
                                  className="start-price-input"
                                  value={startPriceTypedValue}
                                  onUserInput={onStartPriceInput}
                                />
                              </OutlineCard>
                              <RowBetween style={{ backgroundColor: theme.bg1, padding: '12px', borderRadius: '12px' }}>
                                <TYPE.main textAlign="center">Current {baseCurrency?.symbol} Price:</TYPE.main>
                                <TYPE.main textAlign="center">
                                  {price ? (
                                    <TYPE.main textAlign="center">
                                      {invertPrice ? price?.invert()?.toSignificant(8) : price?.toSignificant(8)}{' '}
                                      {quoteCurrency?.symbol}
                                    </TYPE.main>
                                  ) : (
                                    '-'
                                  )}
                                </TYPE.main>
                              </RowBetween>
                              <BlueCard
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  padding: ' 1.5rem 1.25rem',
                                }}
                              >
                                <AlertCircle
                                  color={theme.text1}
                                  size={32}
                                  style={{ marginBottom: '12px', opacity: 0.8 }}
                                />
                                <TYPE.body
                                  fontSize={14}
                                  style={{ marginBottom: 8, fontWeight: 500, opacity: 0.8 }}
                                  textAlign="center"
                                >
                                  You are the first liquidity provider for this Hyperswap V3 pool.
                                </TYPE.body>
                              </BlueCard>
                            </AutoColumn>
                          </DynamicSection>
                        )}

                        {hasExistingPosition && existingPosition ? (
                          <PositionPreview position={existingPosition} title={'Selected Range'} inRange={!outOfRange} />
                        ) : (
                          <>
                            <DynamicSection
                              gap="md"
                              disabled={!feeAmount || invalidPool || (noLiquidity && !startPriceTypedValue)}
                            >
                              <RowBetween>
                                <TYPE.label>{t('selectLiquidityRange')}</TYPE.label>
                                {baseCurrency && quoteCurrency ? (
                                  <RateToggle
                                    currencyA={baseCurrency}
                                    currencyB={quoteCurrency}
                                    handleRateToggle={() => {
                                      onLeftRangeInput('')
                                      onRightRangeInput('')
                                      history.push(
                                        `/add/${currencyIdB as string}/${currencyIdA as string}${
                                          feeAmount ? '/' + feeAmount : ''
                                        }`
                                      )
                                    }}
                                  />
                                ) : null}
                              </RowBetween>
                              <TYPE.main
                                fontSize={14}
                                fontWeight={400}
                                style={{ marginBottom: '.5rem', lineHeight: '125%' }}
                              >
                                Your liquidity will only earn fees when the market price of the pair is within your
                                range.{' '}
                              </TYPE.main>

                              <RangeSelector
                                priceLower={priceLower}
                                priceUpper={priceUpper}
                                getDecrementLower={getDecrementLower}
                                getIncrementLower={getIncrementLower}
                                getDecrementUpper={getDecrementUpper}
                                getIncrementUpper={getIncrementUpper}
                                onLeftRangeInput={onLeftRangeInput}
                                onRightRangeInput={onRightRangeInput}
                                currencyA={baseCurrency}
                                currencyB={quoteCurrency}
                                feeAmount={feeAmount}
                                setMaxPriceRange={setMaxPriceRange}
                              />

                              {price && baseCurrency && quoteCurrency && !noLiquidity && (
                                <LightCard style={{ padding: '12px', overflow: 'auto' }}>
                                  <AutoColumn gap="4px">
                                    <TYPE.main fontWeight={500} textAlign="center" fontSize={12}>
                                      Current Price
                                    </TYPE.main>
                                    <TYPE.body
                                      fontWeight={500}
                                      textAlign="center"
                                      fontSize={20}
                                      style={{ textAlign: 'center' }}
                                    >
                                      {invertPrice ? price.invert().toSignificant(3) : price.toSignificant(3)}{' '}
                                    </TYPE.body>
                                    <TYPE.main fontWeight={500} textAlign="center" fontSize={12}>
                                      {quoteCurrency?.symbol} {' / '}
                                      {baseCurrency.symbol}
                                    </TYPE.main>
                                  </AutoColumn>
                                </LightCard>
                              )}

                              {outOfRange ? (
                                <YellowCard padding="8px 12px" borderRadius="12px">
                                  <RowBetween>
                                    <AlertTriangle stroke={theme.yellow3} size="16px" />
                                    <TYPE.yellow ml="12px" fontSize="12px">
                                      {t('inactiveRangeWarning')}
                                    </TYPE.yellow>
                                  </RowBetween>
                                </YellowCard>
                              ) : null}

                              {invalidRange ? (
                                <YellowCard padding="8px 12px" borderRadius="12px">
                                  <RowBetween>
                                    <AlertTriangle stroke={theme.yellow3} size="16px" />
                                    <TYPE.yellow ml="12px" fontSize="12px">
                                      {t('invalidRangeWarning')}
                                    </TYPE.yellow>
                                  </RowBetween>
                                </YellowCard>
                              ) : null}
                            </DynamicSection>
                          </>
                        )}

                        <ButtonPrimary
                          disabled={!step1Validate}
                          onClick={() => {
                            handleStep(2)
                          }}
                        >
                          Continue
                        </ButtonPrimary>
                      </>
                    )}

                    {activeStep > 1 && step1Validate && (
                      <StepReview>
                        <span className="title">Step 2</span>
                        <div className="value">
                          {isMaxPriceRange ? (
                            'Max price range'
                          ) : (
                            <>
                              {' '}
                              {formatPriceString(priceLower?.toSignificant(5))} -{' '}
                              {formatPriceString(priceUpper?.toSignificant(5))}
                            </>
                          )}
                        </div>
                        <button onClick={() => handleStep(1)}>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1" />
                            <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z" />
                            <path d="M16 5l3 3" />
                          </svg>
                          Edit
                        </button>
                      </StepReview>
                    )}

                    {activeStep === 2 && (
                      <>
                        {pairDoesNotExists ? (
                          <>
                            <ColumnCenter>
                              <BlueCard style={{ maxWidth: '500px' }}>
                                <AutoColumn gap="10px">
                                  <TYPE.link fontWeight={600} color={'primaryText1'}>
                                    You are the first liquidity provider.
                                  </TYPE.link>
                                </AutoColumn>
                              </BlueCard>
                            </ColumnCenter>
                          </>
                        ) : (
                          <></>
                        )}
                        <AutoColumn gap="md" style={{ maxWidth: '500px' }}>
                          <RowBetween paddingBottom="20px">
                            <TYPE.label>Initialize the Pool</TYPE.label>
                          </RowBetween>
                          {pairDoesNotExists ? (
                            <TYPE.link fontWeight={400} color={'text2'}>
                              This pool does not exist yet on the protocol. To initialize it, you need to sign a message
                              with your wallet. This signature is completely gasless (no transaction fees) and allows
                              the pair to be created on the network.
                            </TYPE.link>
                          ) : (
                            <TYPE.link fontWeight={400} color={'text2'}>
                              This pool has already been initialized on the protocol. This step is not required, and you
                              can proceed to the next step.
                            </TYPE.link>
                          )}
                        </AutoColumn>
                        <AutoColumn gap="md" style={{ maxWidth: '500px' }}>
                          <ButtonPrimary
                            disabled={!pairDoesNotExists || loadingCreate || successCreate}
                            onClick={() => onCreate()}
                          >
                            {loadingCreate ? <Dots>Loading</Dots> : 'Sign to initialize the pool'}
                          </ButtonPrimary>
                          <ButtonLight disabled={!step2Validate} onClick={() => handleStep(3)}>
                            Continue
                          </ButtonLight>

                          {errorCreate.length > 0 && !step2Validate && (
                            <TYPE.link fontWeight={400} color={'error'}>
                              {errorCreate}
                            </TYPE.link>
                          )}
                        </AutoColumn>
                      </>
                    )}

                    {activeStep > 2 && step2Validate && (
                      <StepReview>
                        <span className="title">Step 3</span>
                        <div className="value">Pool Has Been Initialized</div>
                        <button onClick={() => handleStep(2)}>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1" />
                            <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z" />
                            <path d="M16 5l3 3" />
                          </svg>
                          Edit
                        </button>
                      </StepReview>
                    )}

                    {activeStep === 3 && (
                      <>
                        <DynamicSection
                          disabled={tickLower === undefined || tickUpper === undefined || invalidPool || invalidRange}
                        >
                          <AutoColumn gap="md">
                            <TYPE.label>{hasExistingPosition ? 'Add more liquidity' : t('depositAmounts')}</TYPE.label>

                            <CurrencyInputPanel
                              value={formattedAmounts[Field.CURRENCY_A]}
                              onUserInput={onFieldAInput}
                              onMax={() => {
                                onFieldAInput(maxAmounts[Field.CURRENCY_A]?.toExact() ?? '')
                              }}
                              showMaxButton={!atMaxAmounts[Field.CURRENCY_A]}
                              currency={currencies[Field.CURRENCY_A]}
                              id="add-liquidity-input-tokena"
                              fiatValue={usdcValues[Field.CURRENCY_A]}
                              showCommonBases
                              locked={depositADisabled}
                            />

                            <CurrencyInputPanel
                              value={formattedAmounts[Field.CURRENCY_B]}
                              onUserInput={onFieldBInput}
                              onMax={() => {
                                onFieldBInput(maxAmounts[Field.CURRENCY_B]?.toExact() ?? '')
                              }}
                              showMaxButton={!atMaxAmounts[Field.CURRENCY_B]}
                              fiatValue={usdcValues[Field.CURRENCY_B]}
                              currency={currencies[Field.CURRENCY_B]}
                              id="add-liquidity-input-tokenb"
                              showCommonBases
                              locked={depositBDisabled}
                            />
                          </AutoColumn>
                        </DynamicSection>
                        <div>
                          {addIsUnsupported ? (
                            <ButtonPrimary disabled={true} borderRadius="12px" padding={'12px'}>
                              <TYPE.main mb="4px">{t('unsupportedAsset')}</TYPE.main>
                            </ButtonPrimary>
                          ) : !account ? (
                            <ButtonLight onClick={toggleWalletModal} borderRadius="12px" padding={'12px'}>
                              {t('connectWallet')}
                            </ButtonLight>
                          ) : (
                            <AutoColumn gap={'md'}>
                              {(approvalA === ApprovalState.NOT_APPROVED ||
                                approvalA === ApprovalState.PENDING ||
                                approvalB === ApprovalState.NOT_APPROVED ||
                                approvalB === ApprovalState.PENDING) &&
                                isValid && (
                                  <RowBetween>
                                    {showApprovalA && (
                                      <ButtonPrimary
                                        borderRadius="12px"
                                        padding={'12px'}
                                        onClick={approveACallback}
                                        disabled={approvalA === ApprovalState.PENDING}
                                        width={showApprovalB ? '48%' : '100%'}
                                      >
                                        {approvalA === ApprovalState.PENDING ? (
                                          <Dots>Approving {currencies[Field.CURRENCY_A]?.symbol}</Dots>
                                        ) : (
                                          'Approve ' + currencies[Field.CURRENCY_A]?.symbol
                                        )}
                                      </ButtonPrimary>
                                    )}
                                    {showApprovalB && (
                                      <ButtonPrimary
                                        borderRadius="12px"
                                        padding={'12px'}
                                        onClick={approveBCallback}
                                        disabled={approvalB === ApprovalState.PENDING}
                                        width={showApprovalA ? '48%' : '100%'}
                                      >
                                        {approvalB === ApprovalState.PENDING ? (
                                          <Dots>Approving {currencies[Field.CURRENCY_B]?.symbol}</Dots>
                                        ) : (
                                          'Approve ' + currencies[Field.CURRENCY_B]?.symbol
                                        )}
                                      </ButtonPrimary>
                                    )}
                                  </RowBetween>
                                )}
                              <ButtonError
                                onClick={() => {
                                  expertMode ? onAdd() : setShowConfirm(true)
                                }}
                                disabled={
                                  !isValid ||
                                  (approvalA !== ApprovalState.APPROVED && !depositADisabled) ||
                                  (approvalB !== ApprovalState.APPROVED && !depositBDisabled)
                                }
                                error={
                                  !isValid && !!parsedAmounts[Field.CURRENCY_A] && !!parsedAmounts[Field.CURRENCY_B]
                                }
                              >
                                <Text fontWeight={500}>{errorMessage ? errorMessage : 'Add'}</Text>
                              </ButtonError>
                            </AutoColumn>
                          )}
                        </div>
                      </>
                    )}
                  </AutoColumn>
                </Wrapper>
              </CustomAppBody>
            </RowContainer>
          </AutoColumn>

          {addIsUnsupported && (
            <UnsupportedCurrencyFooter
              show={addIsUnsupported}
              currencies={[currencies.CURRENCY_A, currencies.CURRENCY_B]}
            />
          )}
        </ScrollablePage>
      </div>
    </>
  )
}
